import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  ArrowRight,
  ArrowUpRight,
  BookOpen,
  CheckCheck,
  ChevronRight,
  Clapperboard,
  Film,
  LayoutGrid,
  LoaderCircle,
  Plus,
  ShieldCheck,
  Sparkles,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Status, Modal, Poster, Loading } from '../components/common';
import { useLab } from '../lib/service';
import { canEdit } from '@drama/domain';
import {} from '../shared';

/** Series index, with the dialog that starts a new one. */
export function NewSeries({
  onClose,
  onError,
}: {
  onClose: () => void;
  onError: (e: unknown) => void;
}) {
  const lab = useLab(),
    navigate = useNavigate();
  const [idea, setIdea] = useState(''),
    [genre, setGenre] = useState('Mystery'),
    [busy, setBusy] = useState(false);
  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const id = await lab.create({ idea, genre });
      await navigate({ to: '/series/$projectId', params: { projectId: id } });
      onClose();
    } catch (e) {
      onError(e);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal
      title="What story do you want to tell?"
      subtitle="A few sentences are all it takes to start something good."
      onClose={onClose}
    >
      <form onSubmit={submit}>
        <label className="field-label" htmlFor="idea">
          Your idea
        </label>
        <textarea
          id="idea"
          value={idea}
          onChange={(e) => setIdea(e.target.value)}
          placeholder="A night receptionist gets a call from a room that’s been sealed for twenty years…"
          minLength={20}
          maxLength={4000}
          rows={6}
          required
        />
        <div className="field-hint">
          <span>Think characters, a conflict, and a little possibility.</span>
          <span>{idea.length}/4,000</span>
        </div>
        <label className="field-label" htmlFor="genre">
          Genre
        </label>
        <select id="genre" value={genre} onChange={(e) => setGenre(e.target.value)}>
          {['Mystery', 'Romance', 'Family drama', 'Thriller', 'Crime', 'Comedy'].map((g) => (
            <option key={g}>{g}</option>
          ))}
        </select>
        <div className="notice">
          <ShieldCheck size={18} />
          <span>Creating a concept is free. Production starts only when you request it.</span>
        </div>
        <div className="modal-actions">
          <Button variant="ghost" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={busy || idea.trim().length < 20}>
            {busy ? <LoaderCircle className="spin" size={17} /> : <ArrowRight size={17} />}Create
            concept
          </Button>
        </div>
      </form>
    </Modal>
  );
}
export function Home({ onCreate, search }: { onCreate: () => void; search: string }) {
  const { home, seedConcepts } = useLab();
  const [filter, setFilter] = useState('All series');
  if (!home) return <Loading />;
  const projects = home.projects.filter(
    (p) =>
      (filter === 'All series' ||
        (filter === 'In production'
          ? p.phase === 'PRODUCTION'
          : filter === 'In development'
            ? ['IDEA', 'PITCH', 'BLUEPRINT', 'CASTING'].includes(p.phase)
            : p.phase === 'ACCEPTED')) &&
      `${p.title} ${p.genre}`.toLowerCase().includes(search.toLowerCase()),
  );
  const featured = projects.filter((p) => p.phase !== 'IDEA').slice(0, 3);
  const others = projects.filter((p) => !featured.includes(p));
  const conceptRows = others.map((p) => (
    <Link key={p._id} to="/series/$projectId" params={{ projectId: p._id }} className="concept-row">
      <span className="concept-symbol">
        <BookOpen size={20} />
      </span>
      <span className="concept-row-title">
        <strong>{p.title}</strong>
        <small>{p.idea}</small>
      </span>
      <span className="genre-tag">{p.genre}</span>
      <Status value={p.phase === 'IDEA' ? 'CONCEPT' : p.phase} />
      <ArrowUpRight size={17} />
    </Link>
  ));
  return (
    <>
      <div className="page-heading">
        <div>
          <span className="eyebrow">THE PRODUCTION ROOM</span>
          <h1>
            Stories, in the making<span>.</span>
          </h1>
          <p>A little imagination. A whole world waiting to happen.</p>
        </div>
        <Button onClick={onCreate} disabled={!canEdit(home.access.role)}>
          <Plus size={17} />
          Develop a series
        </Button>
      </div>
      <div className="overview-stats">
        <div>
          <span className="stat-icon">
            <Film size={18} />
          </span>
          <strong>{home.projects.length.toString().padStart(2, '0')}</strong>
          <span>Series concepts</span>
        </div>
        <div>
          <span className="stat-icon">
            <Clapperboard size={18} />
          </span>
          <strong>
            {home.projects
              .filter((p) => p.phase === 'PRODUCTION')
              .length.toString()
              .padStart(2, '0')}
          </strong>
          <span>In production</span>
        </div>
        <div>
          <span className="stat-icon">
            <CheckCheck size={18} />
          </span>
          <strong>
            {home.projects
              .filter((p) => p.phase === 'ACCEPTED')
              .length.toString()
              .padStart(2, '0')}
            <small> / 03</small>
          </strong>
          <span>Pilot target</span>
        </div>
        <Link to="/experiment" className="experiment-link">
          <span className="small-badge">PHASE 0</span>
          <span>
            The first three pilots<small>Small beginnings. Big possibilities.</small>
          </span>
          <ArrowUpRight size={18} />
        </Link>
      </div>
      <div className="section-heading">
        <div className="filter-tabs">
          {['All series', 'In development', 'In production', 'Completed'].map((f) => (
            <button className={filter === f ? 'selected' : ''} key={f} onClick={() => setFilter(f)}>
              {f}
              {f === 'All series' && <span>{home.projects.length}</span>}
            </button>
          ))}
        </div>
        <span className="view-label">
          <LayoutGrid size={15} />
          Gallery view
        </span>
      </div>
      {featured.length > 0 && (
        <div className="series-grid">
          {featured.map((p) => (
            <Link
              key={p._id}
              to="/series/$projectId"
              params={{ projectId: p._id }}
              className="series-card"
            >
              <div className="poster-wrap">
                <Poster project={p} />
                <span className="poster-open">
                  <ArrowUpRight size={19} />
                </span>
              </div>
              <div className="series-info">
                <div>
                  <h3>{p.title}</h3>
                  <span className="series-subline">
                    {p.genre}
                    <i />
                    Season 01
                  </span>
                </div>
                <ArrowUpRight size={18} aria-hidden="true" />
              </div>
              <div className="series-card-footer">
                <Status
                  value={
                    p.phase === 'PRODUCTION'
                      ? 'IN_PRODUCTION'
                      : p.phase === 'PITCH'
                        ? 'PITCH_READY'
                        : p.phase
                  }
                />
                <span>
                  {p.castLocked ? 'Cast locked' : 'Developing the story'}
                  <ChevronRight size={13} />
                </span>
              </div>
            </Link>
          ))}
        </div>
      )}
      {others.length > 0 && (
        <>
          <div className="ideas-heading">
            <h2>
              On the drawing board <span>{others.length}</span>
            </h2>
            <p>Every idea deserves a first chapter.</p>
          </div>
          <div className="concept-list">
            {search.trim() ? (
              conceptRows
            ) : (
              <>
                {conceptRows.slice(0, 3)}
                {others.length > 3 && (
                  <details className="more-concepts">
                    <summary>Show {others.length - 3} more concepts</summary>
                    {conceptRows.slice(3)}
                  </details>
                )}
              </>
            )}
          </div>
        </>
      )}
      {!projects.length && (
        <div className="empty-state">
          <Film size={32} />
          <h2>{search ? 'No matching stories' : 'The next great story could be yours.'}</h2>
          <p>
            {search
              ? 'Try another title or genre.'
              : 'Begin with a few sentences. We’ll take it from there.'}
          </p>
          <Button onClick={onCreate} disabled={!canEdit(home.access.role)}>
            <Plus size={16} />
            Start a series
          </Button>
          {!search && canEdit(home.access.role) && (
            <Button variant="outline" onClick={() => void seedConcepts()}>
              Load the ten experiment concepts
            </Button>
          )}
        </div>
      )}
      <div className="bottom-note">
        <span className="tiny-logo">
          <Sparkles size={14} />
        </span>
        <span>Imagine a story. Make it a series. Find an audience.</span>
        <span>BUILT FOR THE STORYTELLERS</span>
      </div>
    </>
  );
}
